import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom"; // Add this import
import { AlertCircle, CheckCircle2, Clock, Code, GitPullRequest, Settings, TestTube, Wrench } from "lucide-react";

interface StepStatus {
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
  active: boolean;
  failed: boolean;
  stopping: boolean;
  stopped: boolean;
}

interface GenerationStepperProps {
  currentStep: string;
  timeRemaining: number;
  isPRScenario?: boolean;
  onCancel?: () => void;
  showExtendedMessage?: boolean;
  failedStep?: string;
  generationState: "ACTIVE" | "DONE" | "FAILED" | "STOPPING" | "STOPPED";
  hasError?: boolean;
  onResume?: () => void;
  resumeLoading?: boolean;
}

const GenerationStepper: React.FC<GenerationStepperProps> = ({
                                                               currentStep,
                                                               timeRemaining,
                                                               isPRScenario = false,
                                                               onCancel,
                                                               showExtendedMessage = false,
                                                               failedStep,
                                                               generationState,
                                                               hasError = false,
                                                               onResume,
                                                               resumeLoading = false
                                                             }) => {
  const [steps, setSteps] = useState<StepStatus[]>([]);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [localTimeRemaining, setLocalTimeRemaining] = useState<number>(0);
  const [showStopModal, setShowStopModal] = useState(false);
  
  // Define steps for regular scenario
  const regularSteps = [
    {
      key: "GENERATING_UML",
      title: "Project Initialization",
      description: "Buckle up! We're igniting the engines, setting up the environment, and prepping everything needed to launch this shred into orbit. The journey begins now!",
      icon: <Settings className="w-5 h-5" />
    },
    {
      key: "GENERATING_CODE",
      title: "Code Generation",
      description: "We're in the zone! Crafting code that not only meets your requirements but dances to the rhythm of best practices. Clean, precise, and blazing fast.",
      icon: <Code className="w-5 h-5" />
    },
    {
      key: "COMPILING_CODE",
      title: "Code Compilation",
      description: "Compiling like pros—turning code into a working masterpiece. We're ironing out bugs and ensuring everything runs smoother than silk.",
      icon: <Wrench className="w-5 h-5" />
    },
    {
      key: "GENERATING_TESTS",
      title: "Testing & Validation",
      description: "We're live-testing your app in a sandbox, checking every workflow end-to-end. It takes time—but it's worth it to ship production-ready code.",
      icon: <TestTube className="w-5 h-5" />
    }
  ];
  
  // Define steps for PR scenario
  const prSteps = [
    {
      key: "ADDRESSING_PR_REVIEW",
      title: "Addressing PR Review",
      description: "We're analyzing the pull request feedback and preparing the necessary changes.",
      icon: <GitPullRequest className="w-5 h-5" />
    },
    {
      key: "COMPILING_CODE",
      title: "Code Compilation",
      description: "We are now compiling the updated code, making sure to remove any bugs or errors.",
      icon: <Wrench className="w-5 h-5" />
    },
    {
      key: "GENERATING_TESTS",
      title: "Testing & Validation",
      description: "We're running comprehensive tests to ensure all changes work perfectly.",
      icon: <TestTube className="w-5 h-5" />
    }
  ];
  
  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds) || seconds <= 0) return "0:00:00";
    
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };
  
  // Update local time when timeRemaining prop changes
  useEffect(() => {
    const validTime = timeRemaining && !isNaN(timeRemaining) ? timeRemaining : 0;
    setLocalTimeRemaining(validTime);
  }, [timeRemaining]);
  
  // Countdown timer effect
  useEffect(() => {
    if (localTimeRemaining <= 0 || currentStep === "DONE") return;
    
    const timer = setInterval(() => {
      setLocalTimeRemaining(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [localTimeRemaining, currentStep]);
  
  useEffect(() => {
    const stepList = isPRScenario ? prSteps : regularSteps;
    
    const updatedSteps = stepList.map((step, index) => {
      const isCompleted = completedSteps.has(step.key);
      const isActive = step.key === currentStep && generationState === "ACTIVE";
      const isFailed = failedStep === step.key || (hasError && step.key === currentStep);
      const isStopping = step.key === currentStep && generationState === "STOPPING";
      const isStopped = step.key === currentStep && generationState === "STOPPED";
      
      // Mark previous steps as completed if current step is further along
      const currentIndex = stepList.findIndex(s => s.key === currentStep);
      const shouldBeCompleted = index < currentIndex || currentStep === "DONE";
      
      return {
        ...step,
        completed: isCompleted || (shouldBeCompleted && !isFailed),
        active: isActive,
        failed: isFailed,
        stopping: isStopping,
        stopped: isStopped
      };
    });
    
    setSteps(updatedSteps);
    
    // Add completed animation effect
    if (currentStep !== "DONE" && !hasError) {
      const currentIndex = stepList.findIndex(s => s.key === currentStep);
      if (currentIndex > 0) {
        const previousStep = stepList[currentIndex - 1];
        if (!completedSteps.has(previousStep.key)) {
          setCompletedSteps(prev => new Set([...prev, previousStep.key]));
        }
      }
    }
  }, [currentStep, isPRScenario, completedSteps, failedStep, hasError, generationState]);
  
  const allCompleted = currentStep === "DONE";
  const isStopping = generationState === "STOPPING";
  const isStopped = generationState === "STOPPED";
  
  return (
    <div className="w-full max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-sm">
      {/* Notification Area */}
      {!allCompleted && (
        <div className="mt-6 max-w-2xl mx-auto">
          <div
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-white to-indigo-50 border border-blue-200 shadow-lg">
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-blue-200/30 to-indigo-200/30 rounded-full blur-xl animate-pulse"></div>
              <div
                className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-tr from-indigo-200/30 to-blue-200/30 rounded-full blur-xl animate-pulse delay-700"></div>
            </div>
            
            <div className="relative p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div
                    className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg animate-bounce">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                </div>
                
                <div className="flex-1">
                  <h3
                    className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent mb-2">
                    We'll Notify You When It's Ready!
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Your code is being generated by our AI. You'll receive a notification as soon as it's complete.
                  </p>
                  
                  <div className="flex flex-wrap gap-3">
                    <div
                      className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
                      Email Notification
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Container with Two Independent Sections */}
      
      <div className="flex gap-6 hidden">
        {/* Left Section - Independent for Badges */}
        <div className="w-80 flex-shrink-0">
          <div className="space-y-8">
            {steps.map((step) => (
              <div key={step.key} className="h-20 flex items-center justify-center pt-10">
                {/* Timer Badge for Active Step */}
                {step.active && !allCompleted && (
                  <div className="w-full flex justify-center">
                    {showExtendedMessage ? (
                      <div
                        className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-3 rounded-full text-sm font-semibold shadow-lg w-72 text-center">
                        <Clock className="w-4 h-4 inline-block mr-2" />
                        Taking longer than expected, but we're making excellent progress! Quality takes time.
                      </div>
                    ) : (
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-full text-lg font-semibold shadow-lg">
                        <Clock className="w-5 h-5 inline-block mr-2" />
                        {formatTime(localTimeRemaining)}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Error Badge for Failed Step */}
                {step.failed && (
                  <div className="w-full flex justify-center">
                    <div className="bg-white rounded-2xl shadow-2xl border border-red-100 p-4 flex items-center gap-4">
                      {/* Failed Badge */}
                      <div
                        className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-lg flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        <span>Failed</span>
                      </div>
                      
                      {/* Resume Button */}
                      {onResume && (
                        <button
                          onClick={onResume}
                          disabled={resumeLoading}
                          className="group relative bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-green-300 disabled:to-green-400 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-lg transition-all duration-300 flex items-center gap-2 transform hover:scale-105 disabled:hover:scale-100"
                        >
                          <div className="relative">
                            {resumeLoading ? (
                              <div
                                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            )}
                          </div>
                          <span className="relative">
                                                        {resumeLoading ? "Resuming..." : "Resume"}
                                                    </span>
                          <div
                            className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-400 to-green-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-sm"></div>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Right Section - Stepper */}
        <div className="flex-1">
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            <div
              className="absolute left-6 top-0 w-0.5 bg-gradient-to-b from-blue-500 to-purple-600 transition-all duration-1000 ease-out"
              style={{
                height: allCompleted ? "100%" : `${(steps.filter(s => s.completed).length / steps.length) * 100}%`
              }}
            ></div>
            
            {/* Steps */}
            <div className="space-y-8">
              {steps.map((step) => (
                <div key={step.key} className="relative flex items-start gap-4">
                  {/* Step Icon */}
                  <div className={
                    `relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-500
                    ${step.failed ?
                      "bg-red-500 border-red-500 text-white"
                      : step.completed ?
                        "bg-green-500 border-green-500 text-white"
                        :
                        step.active ?
                          "bg-gradient-to-r from-blue-500 to-purple-600 border-blue-500 text-white animate-pulse-smooth"
                          :
                          "bg-gray-100 border-gray-300 text-gray-400"
                    }
                    `}>
                    {step.failed ? (
                      <AlertCircle className="w-6 h-6" />
                    ) : step.completed ? (
                      <CheckCircle2 className="w-6 h-6" />
                    ) : (
                      step.icon
                    )}
                  </div>
                  
                  {/* Step Content */}
                  <div className="flex-1 min-w-0">
                    <div className={`
                    transition-all duration-300
                    ${step.active ? "transform translate-x-2" : ""}
                    `}>
                      <h3 className={`
                      text-lg font-semibold mb-1 transition-colors duration-300
                      ${step.failed ?
                        "text-red-700"
                        :
                        step.completed ?
                          "text-green-700"
                          :
                          step.active ?
                            "text-blue-700"
                            :
                            "text-gray-500"
                      }
                      `}>
                        {step.title}
                      </h3>
                      <p className={`
                      text-sm leading-relaxed transition-colors duration-300
                      ${step.failed ?
                        "text-red-600"
                        :
                        step.active ?
                          "text-gray-700"
                          :
                          "text-gray-500"
                      }
                      `}>
                        {step.description}
                      </p>
                    </div>
                    
                    {/* Active Step Indicator */}
                    {step.active && !step.failed && (
                      <div className="mt-3 flex items-center gap-2 text-blue-600">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                               style={{ animationDelay: "0.1s" }}></div>
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                               style={{ animationDelay: "0.2s" }}></div>
                        </div>
                        <span className="text-sm font-medium">In Progress</span>
                      </div>
                    )}
                    
                    {/* Failed Step Indicator */}
                    {step.failed && (
                      <div className="mt-3 flex items-center gap-2 text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Step Failed</span>
                      </div>
                    )}
                    
                    {/* Completion Animation */}
                    {step.completed && !step.failed && (
                      <div className="mt-2 text-green-600 text-sm font-medium flex items-center gap-2">
                        <span>✨ Completed</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <div className="space-y-2">
        {steps.map((step) => (
          <div key={step.key} className="flex gap-6 items-start">
            {/* Left Section - Badge Area */}
            <div className="w-[340px] flex-shrink-0 pt-2.5 flex items-start justify-center">
              {/* Timer Badge for Active Step */}
              {step.active && !allCompleted && (
                <>
                  {showExtendedMessage ? (
                    <div className="w-full flex justify-end pt-1.5">
                      <div
                        className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-3 rounded-full text-sm font-semibold shadow-lg w-fit text-center">
                        <Clock className="w-4 h-4 inline-block mr-2 mb-1" />
                        Taking longer than expected, but we're making excellent progress! Quality takes time.
                      </div>
                    </div>
                  ) : (
                    <div className="w-full flex justify-end pt-4">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-full text-lg font-semibold shadow-lg">
                        <Clock className="w-5 h-5 inline-block mr-2 mb-1" />
                        {formatTime(localTimeRemaining)}
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Error Badge for Failed Step */}
              {step.failed && true && (
                <div className="w-full flex justify-end">
                  <div className="bg-white rounded-2xl shadow-2xl border border-red-100 p-4 flex items-center gap-4">
                    {/* Failed Badge */}
                    <div
                      className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-lg flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <span>Failed</span>
                    </div>
                    
                    {/* Resume Button */}
                    {onResume && (
                      <button
                        onClick={onResume}
                        disabled={resumeLoading}
                        className="group relative bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-green-300 disabled:to-green-400 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-lg transition-all duration-300 flex items-center gap-2 transform hover:scale-105 disabled:hover:scale-100"
                      >
                        <div className="relative">
                          {resumeLoading ? (
                            <div
                              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          )}
                        </div>
                        <span className="relative">
                          {resumeLoading ? "Resuming..." : "Resume"}
                        </span>
                        <div
                          className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-400 to-green-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-sm"></div>
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Step Stopping Badge */}
              {step.stopping && (
                <>
                  <div className="w-full flex justify-end pt-4">
                    <div
                      className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                      <Clock className="w-5 h-5 inline-block mr-2 mb-1" />
                      The Shred is stopping, please wait...
                    </div>
                  </div>
                </>
              )}
              
              {/* Step Stopped Badge whith resume option */}
              {step.stopped && (
                <div className="w-full flex justify-end">
                  <div className="bg-white rounded-2xl shadow-2xl border border-red-100 p-4 flex items-center gap-4">
                    {/* Stopped Badge in a different color the the fail badge */}
                    <div
                      className="bg-gradient-to-r from-cyan-400 to-cyan-600 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-lg flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <span>Stopped</span>
                    </div>
                    
                    {/* Resume Button */}
                    {onResume && (
                      <button
                        onClick={onResume}
                        disabled={resumeLoading}
                        className="group relative bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-green-300 disabled:to-green-400 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-lg transition-all duration-300 flex items-center gap-2 transform hover:scale-105 disabled:hover:scale-100"
                      >
                        <div className="relative">
                          {resumeLoading ? (
                            <div
                              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          )}
                        </div>
                        <span className="relative">
                          {resumeLoading ? "Resuming..." : "Resume"}
                        </span>
                        <div
                          className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-400 to-green-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-sm"></div>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Right Section - Step Content */}
            <div className="flex-1 relative pt-6">
              {/* Progress Line - only show for non-last steps */}
              {steps.indexOf(step) < steps.length - 1 && (
                <>
                  <div className="absolute left-[22px] top-12 w-0.5 bg-gray-200 -mb-8 bottom-0"></div>
                  <div
                    className={`absolute left-[22px] top-12 w-0.5 bg-gradient-to-b from-blue-500 to-purple-600 transition-all duration-1000 ease-out -mb-8 ${
                      step.completed ? "bottom-0" : "h-0"
                    }`}
                  ></div>
                </>
              )}
              
              <div className="flex items-start gap-4">
                {/* Step Icon */}
                <div className={
                  `relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-500 z-10
                    ${step.failed ?
                    "bg-red-500 border-red-500 text-white"
                    :
                    step.completed ?
                      "bg-green-500 border-green-500 text-white"
                      :
                      step.active ?
                        "bg-gradient-to-r from-blue-500 to-purple-600 border-blue-500 text-white animate-pulse-smooth"
                        :
                        step.stopping ?
                          "bg-yellow-500 border-yellow-500 text-white"
                          :
                          step.stopped ?
                            "bg-cyan-400 border-cyan-400 text-white"
                            :
                            "bg-gray-100 border-gray-300 text-gray-400"
                  }
                  `}>
                  {step.failed ? (
                    <AlertCircle className="w-6 h-6" />
                  ) : step.completed ? (
                    <CheckCircle2 className="w-6 h-6" />
                  ) : (
                    step.icon
                  )}
                </div>
                
                {/* Step Content */}
                <div className="flex-1 pt-2.5 min-w-0">
                  <div className={`transition-all duration-300
                    ${step.active ? "transform translate-x-2" : ""}
                  `}>
                    <h3 className={`text-lg font-semibold mb-1 transition-colors duration-300
                    ${step.failed ?
                      "text-red-700"
                      :
                      step.completed ?
                        "text-green-700"
                        :
                        step.active ?
                          "text-blue-700"
                          :
                          step.stopping ?
                            "text-yellow-700"
                            :
                            step.stopped ?
                              "text-cyan-700"
                              :
                              "text-gray-500"
                    }
                  `}>
                      {step.title}
                    </h3>
                    <p className={`text-sm leading-relaxed transition-colors duration-300
                    ${step.failed ?
                      "text-red-600"
                      :
                      step.active ?
                        "text-gray-700"
                        :
                        step.stopping ?
                          "text-yellow-600"
                          :
                          step.stopped ?
                            "text-cyan-600"
                            :
                            "text-gray-500"
                    }
                  `}>
                      {step.description}
                    </p>
                  </div>
                  
                  {/* Active Step Indicator */}
                  {step.active && !step.failed && (
                    <div className="mt-3 flex items-center gap-2 text-blue-600">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                             style={{ animationDelay: "0.1s" }}></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                             style={{ animationDelay: "0.2s" }}></div>
                      </div>
                      <span className="text-sm font-medium">In Progress</span>
                    </div>
                  )}
                  
                  {/* Failed Step Indicator */}
                  {step.failed && (
                    <div className="mt-3 flex items-center gap-2 text-red-600">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Step Failed</span>
                    </div>
                  )}
                  
                  {/* Stopping Step Indicator */}
                  {step.stopping && (
                    <div className="mt-3 flex items-center gap-2 text-yellow-600">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">The Shred is stopping, please wait...</span>
                    </div>
                  )}
                  
                  {/* Stopped Step Indicator */}
                  {step.stopped && (
                    <div className="mt-3 flex items-center gap-2 text-cyan-600">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Stopped</span>
                    </div>
                  )}
                  
                  {/* Completion Animation */}
                  {step.completed && !step.failed && (
                    <div className="mt-2 text-green-600 text-sm font-medium flex items-center gap-2">
                      <span>✨ Completed</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Final Success State */}
      {allCompleted && !hasError && (
        <div className="mt-8 text-center">
          <div
            className="inline-flex items-center gap-3 bg-gradient-to-r from-green-400 to-green-600 text-white px-8 py-4 rounded-full shadow-lg animate-pulse-smooth">
            <CheckCircle2 className="w-6 h-6" />
            <span className="font-bold text-lg">🎉 Your code is ready!</span>
          </div>
        </div>
      )}
      
      {/* Error State */}
      {hasError && (
        <div className="mt-8 text-center">
          <div
            className="inline-flex items-center gap-3 bg-gradient-to-r from-red-400 to-red-600 text-white px-8 py-4 rounded-full shadow-lg">
            <AlertCircle className="w-6 h-6" />
            <span className="font-bold text-lg">Generation Failed</span>
          </div>
        </div>
      )}
      
      {/* Cancel Button */}
      {!allCompleted && !isStopping && !isStopped && onCancel && (
        <div className="mt-8 text-center">
          <button
            onClick={() => setShowStopModal(true)} // Change this line
            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 font-medium"
          >
            Stop Generation
          </button>
        </div>
      )}
      
      {/* Add the message box here - right after the Cancel Button section */}
      {!allCompleted && (
        <div
          className="mt-6 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-lg p-6 shadow-md">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-lg">⏰</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-amber-800 font-semibold text-lg mb-2 flex items-center gap-2">
                Good things take time!
                <span className="text-sm">☕</span>
              </h3>
              <p className="text-amber-700 leading-relaxed">
                The complete journey from idea to <strong>production-ready, tested code</strong> typically takes
                <span className="font-semibold text-amber-800"> 5-10 hours</span>.
                Yes, it's not instant ramen 🍜, but you're getting restaurant-quality code that's ready to deploy!
                Think of it as slow-cooking your software - the result is worth the wait.
                <span className="inline-block ml-1">🚀✨</span>
              </p>
              <div className="mt-3 text-sm text-amber-600 italic">
                💡 Pro tip: Grab a coffee, take a walk, or binge-watch your favorite series while we craft your
                masterpiece!
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Stop Generation Modal */}
      {showStopModal && createPortal(
        <>
          {/* Backdrop with blur effect */}
          <div
            className="fixed inset-0 z-[9998] backdrop-blur-sm bg-black/50"
            onClick={() => setShowStopModal(false)}
          />
          
          {/* Modal container */}
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div
              className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700 transform transition-all duration-300 scale-100"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center animate-pulse">
                    <span className="text-xl">⚠️</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">Hold on a second!</h3>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="space-y-4 text-gray-300">
                  <p className="leading-relaxed">
                    <span className="text-amber-400 font-semibold">Don't worry if it's taking time</span> - this is
                    completely normal!
                    Our AI is crafting production-ready code, and quality takes patience. ⏰
                  </p>
                  
                  <div className="bg-gray-800 rounded-lg p-4 border-l-4 border-blue-500">
                    <p className="text-sm">
                      <span className="text-blue-400 font-medium">✨ Good news:</span> If any errors or blocks occur
                      during
                      generation,
                      they'll be displayed immediately on screen. The generation continues working perfectly even if it
                      takes longer than expected.
                    </p>
                  </div>
                  
                  <p className="text-sm text-gray-400 italic">
                    💡 Think of it like brewing the perfect coffee - rushing ruins the magic! ☕
                  </p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="p-6 border-t border-gray-700 flex gap-3">
                <button
                  onClick={() => setShowStopModal(false)}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-semibold transition-all duration-200 transform hover:scale-105"
                >
                  Continue Waiting ✨
                </button>
                <button
                  onClick={() => {
                    setShowStopModal(false);
                    onCancel?.();
                  }}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg font-medium transition-all duration-200"
                >
                  Stop Anyway
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body // This renders the modal at the body level
      )}
      
      <style>{`
          .animate-pulse-smooth {
              animation: pulse-smooth 3s ease-in-out infinite;
          }

          @keyframes pulse-smooth {
              0%, 100% {
                  opacity: 1;
              }
              50% {
                  opacity: 0.7;
              }
          }
      `}</style>
    </div>
  );
};
export default GenerationStepper;